import { useRecoilValue, useSetRecoilState } from "recoil"
import { Box, Flex, Link } from "@chakra-ui/layout"
import { CgExpand } from "react-icons/cg"
import { FaXmark } from "react-icons/fa6"
import { IconButton, Spacer } from "@chakra-ui/react"
import { HiArrowsExpand } from "react-icons/hi"

import { ElementId, ModNotPresentType } from "../../../constants/enums"
import { brandingSelector } from "../../../recoil/atoms/room"
import useProductPageOverlay from "../../../hooks/useProductPageOverlay"
import useOnClickWithTouchHandler from "../../../hooks/useOnClickWithTouchHandler"
import useMeetingClient from "../../../hooks/useMeetingClient"
import { PlayPauseButton } from "../videoPlayer/controls-player/play-pause-button"
import { playerPropsSelector } from "../../../recoil/atoms/player"
import { useCallback, useState } from "react"
import { useWatchParty, useWatchPartyController } from "../../../hooks/useWatchParty"
import { MuteButtonPlayer } from "../videoPlayer/controls-player/mute-button"
import LiveIcon from "../../common/icons/live-icon"
import { LocalMuteIcon } from "../buttons-main-screen/local-mute-icon"
import { localUserIdAtom } from "../../../recoil/atoms/users"
import { useVideoPopup } from "./useVideoPopup"
import { AvatarMuteIcon } from "../buttons-main-screen/avatar-mute-icon"
import { AvatarMicControl } from "../buttons-main-screen/avatar-mic-control"

const MiniPlayerExpandIcon = () => {
  const roomStyles = useRecoilValue(brandingSelector)
  const { closeProductPageOverlay } = useProductPageOverlay()
  const { toggleVideoPopup } = useVideoPopup()
  const { stopWatchParty } = useWatchPartyController()

  const handleOnClose = (event: React.TouchEvent | React.MouseEvent) => {
    // Close product page overlay if present
    closeProductPageOverlay(event)

    // Close popup avatar 
    toggleVideoPopup(false)
  }

  const handlers = useOnClickWithTouchHandler(handleOnClose)

  return (
    <Link
      {...handlers}
      zIndex="1"
      margin="0 auto"
      padding="10px"
      color={roomStyles?.servicePanelElementBorder ?? "white"}
    >
      <Box
        borderRadius="4px"
        padding="2px"
        background={roomStyles?.servicePanelElementBorderOffset ?? "black"}
        color={roomStyles?.servicePanelElementBorder ?? "white"}
      >
        <HiArrowsExpand size="1.6rem" />
      </Box>
    </Link>
  )
}

const MiniPlayerResizeIcon = () => {
  const roomStyles = useRecoilValue(brandingSelector)

  return (
    <Link
      zIndex="1"
      alignSelf="flex-end"
      color={roomStyles?.overlayEmojiStrokeColor ?? "white"}
      id={ElementId.FLOATING_VIDEO_RESIZE_ICON}
    >
      <CgExpand size="1.2rem" cursor={"nesw-resize"} />
    </Link>
  )
}

const MiniPlayerWatchPartyButtons = () => {
  const { paused } = useRecoilValue(playerPropsSelector)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const video = document.getElementById(
    "watchpartyvideoplayer"
  ) as HTMLVideoElement

  const touchDelay = 150

  const [timeStamp, setTimeStamp] = useState({
    start: 0,
    end: 0
  })

  const playVideo = () => {
    video.play()
    setPlayerProps({
      paused: false,
      isEnabledToggleControlDesktop: true,
      isEnabledAutoPlay: true
    })
  }

  const handleOnTouchEndStop = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      if (event.timeStamp - timeStamp.start < touchDelay) {
        video.pause()
        setPlayerProps({ paused: true })
      }
      setTimeStamp({ start: 0, end: 0 })
    },
    [setPlayerProps, timeStamp.start, touchDelay, video]
  )

  const handleTouchStart = event => {
    event.preventDefault()
    event.stopPropagation()
    setTimeStamp({ ...timeStamp, start: event.timeStamp })
  }

  const pauseVideo = event => {
    event.stopPropagation()
    video.pause()
    setPlayerProps({ paused: true, isEnabledAutoPlay: true })
  }

  const handleOnTouchEndPlay = useCallback(
    async e => {
      e.preventDefault()
      e.stopPropagation()
      if (e.timeStamp - timeStamp.start < touchDelay) {
        video.play()

        setPlayerProps({ paused: false })
        let timer = setTimeout(() => {
          setPlayerProps({ isOpenControlsMobile: false })
        }, 3000)
        return () => clearTimeout(timer)
      }
      setTimeStamp({ start: 0, end: 0 })
    },
    [setPlayerProps, timeStamp.start, touchDelay, video]
  )

  return (
    <Flex>
      <PlayPauseButton
        paused={paused}
        handleTouchStart={handleTouchStart}
        playVideo={playVideo}
        handleOnTouchEndStop={handleOnTouchEndStop}
        pauseVideo={pauseVideo}
        handleOnTouchEndPlay={handleOnTouchEndPlay}
      />
      <LocalMuteIcon />
    </Flex>
  )
}

const MiniPlayerAvatarButtons = () => {
  return (
    <Flex>
      <Flex
        justifyContent="center"
        alignItems="center"
        paddingStart="10px"
        marginBottom="10px"
      >
        <AvatarMicControl />
        <Spacer width={"0.5rem"}/>
        <AvatarMuteIcon />
      </Flex>
    </Flex>
  )
}

const MiniPlayerLiveVideoButtons = () => {
  return (
    <Flex>
      <Flex
        justifyContent="center"
        alignItems="center"
        paddingStart="3"
        paddingEnd="1"
        marginBottom="4px"
      >
        <LiveIcon />
      </Flex>
      <LocalMuteIcon />
    </Flex>
  )
}

const NativeMiniPlayerCloseIcon = () => {
  const roomStyles = useRecoilValue(brandingSelector)
  const meetingClient = useMeetingClient()

  const handlers = useOnClickWithTouchHandler(
    meetingClient?.nativeBridge?.eventMiniPlayerCloseButtonPressed
  )

  return (
    <Link
      {...handlers}
      zIndex="1"
      alignSelf="flex-end"
      margin="0 auto"
      padding="10px"
    >
      <IconButton
        aria-label="Close"
        icon={<FaXmark />}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="32px"
        height="32px"
        minH="32px"
        minW="32px"
        position="relative"
        borderRadius="50%"
        cursor="pointer"
        fontSize="1.3rem"
        zIndex={1000000}
        color={roomStyles?.servicePanelElementBorder ?? "white"}
        background={roomStyles?.servicePanelElementBorderOffset ?? "black"}
    />
    </Link>
  )
}

const MiniPlayerButton = ({ className }) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const { watchPartyHostId } = useWatchParty()
  const localUserId = useRecoilValue(localUserIdAtom)
  const { isAvatarPopupActive } = useVideoPopup()

  // hide Player controller for overlay and image mnp 
  const showPlayerController =  ![ModNotPresentType.OVERLAY, ModNotPresentType.IMAGE].includes(watchPartyHostId as any)

  // show live controller buttons when video is live and watch party is shared by another user
  const showLiveController =
    watchPartyHostId !== ModNotPresentType.VIDEO &&
    watchPartyHostId !== localUserId

  const renderOnVideoButtons = () => {
    if(!showPlayerController) return null

    if (isAvatarPopupActive) 
      return <MiniPlayerAvatarButtons />

    if (showLiveController)
      return <MiniPlayerLiveVideoButtons />

    return <MiniPlayerWatchPartyButtons />
  }

  const renderTopRight = () => {
    // if(USE_MOBILE_UI) return <NativeMiniPlayerCloseIcon />
    return <MiniPlayerResizeIcon />
  }

  return (
    <Box 
      pos="absolute" 
      left={0} 
      width="100%" 
      height="100%" 
      background="transparent"  
      zIndex="2147483647"
      overflow={"hidden"}
      >
      <Flex
        pos="absolute"
        left="0"
        bottom="0"
        direction="column"
        justifyContent="space-between"
        zIndex="2147483647"
        color={roomStyles?.servicePanelElementBorder ?? "white"}
        className={className}
      >
        { renderOnVideoButtons() }
      </Flex>

      <Flex
        pos="absolute"
        right="0"
        direction="column"
        justifyContent="space-between"
        height="100%"
        zIndex="2147483647"
        color={roomStyles?.servicePanelElementBorder ?? "white"}
        id={ElementId.FLOATING_VIDEO_BUTTON_CONTAINER}
        className={className}
      >
        { renderTopRight() }
        <MiniPlayerExpandIcon />
      </Flex>
    </Box>
  )
}

export default MiniPlayerButton
